import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Star, Trash2, X } from 'lucide-react';
import { AdminShell } from '@/components/layout/AdminShell';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api-client';
import { useToast } from '@/components/ui/use-toast';
import { cn, formatRelativeDate } from '@/lib/utils';

interface ReviewItem {
  reviewId: string;
  taskId: string;
  lawyerId: string;
  authorId: string;
  rating: number;
  comment?: string;
  lawyerResponse?: string;
  moderationStatus?: 'pending' | 'approved' | 'rejected';
  moderationReason?: string;
  moderatedAt?: string;
  createdAt: string;
}

type Filter = 'pending' | 'approved' | 'rejected' | 'all';

export function AdminAvaliacoes() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [filter, setFilter] = useState<Filter>('pending');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'reviews'],
    queryFn: () => api.get<{ items: ReviewItem[] }>('/admin/reviews?limit=200'),
  });

  const moderateMut = useMutation({
    mutationFn: (params: { r: ReviewItem; decision: 'approved' | 'rejected'; reason?: string }) =>
      api.patch(
        `/admin/lawyers/${params.r.lawyerId}/reviews/${params.r.reviewId}/moderate`,
        params.reason ? { decision: params.decision, reason: params.reason } : { decision: params.decision },
      ),
    onSuccess: (_d, vars) => {
      toast({
        variant: 'success',
        title: vars.decision === 'approved' ? 'Avaliação aprovada' : 'Avaliação rejeitada',
      });
      qc.invalidateQueries({ queryKey: ['admin', 'reviews'] });
      qc.invalidateQueries({ queryKey: ['admin', 'stats'] });
      qc.invalidateQueries({ queryKey: ['lawyers'] });
    },
    onError: (err) =>
      toast({ variant: 'danger', title: 'Falha', description: err instanceof Error ? err.message : 'Erro' }),
  });

  const delMut = useMutation({
    mutationFn: (r: ReviewItem) =>
      api.delete(`/admin/lawyers/${r.lawyerId}/reviews/${r.reviewId}`),
    onSuccess: () => {
      toast({ variant: 'success', title: 'Avaliação removida' });
      qc.invalidateQueries({ queryKey: ['admin', 'reviews'] });
      qc.invalidateQueries({ queryKey: ['admin', 'stats'] });
    },
    onError: (err) =>
      toast({ variant: 'danger', title: 'Falha', description: err instanceof Error ? err.message : 'Erro' }),
  });

  const counts = useMemo(() => {
    const c = { pending: 0, approved: 0, rejected: 0, all: 0 };
    for (const r of data?.items ?? []) {
      c.all++;
      const s = (r.moderationStatus ?? 'pending') as 'pending' | 'approved' | 'rejected';
      c[s]++;
    }
    return c;
  }, [data]);

  const filtered = useMemo(() => {
    const arr = data?.items ?? [];
    if (filter === 'all') return arr;
    return arr.filter((r) => (r.moderationStatus ?? 'pending') === filter);
  }, [data, filter]);

  return (
    <AdminShell>
      <div className="max-w-5xl">
        <h1 className="font-serif text-display-lg">Avaliações</h1>
        <p className="text-muted-foreground mt-1">
          Modere reviews antes de aparecerem no perfil público. Rejeitada nunca aparece e não conta
          no average rating.
        </p>

        <div className="mt-6 flex flex-wrap gap-2">
          {(['pending', 'approved', 'rejected', 'all'] as Filter[]).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors',
                filter === f
                  ? 'border-accent bg-accent/15 text-foreground font-medium'
                  : 'border-border text-muted-foreground hover:border-accent/40',
              )}
            >
              {f === 'pending' && 'Pendentes'}
              {f === 'approved' && 'Aprovadas'}
              {f === 'rejected' && 'Rejeitadas'}
              {f === 'all' && 'Todas'}
              <span className="text-[10px] opacity-60">{counts[f]}</span>
            </button>
          ))}
        </div>

        <div className="mt-6 space-y-3">
          {isLoading && <p className="text-muted-foreground">Carregando…</p>}
          {data && filtered.length === 0 && (
            <Card>
              <CardContent className="pt-6 text-muted-foreground text-sm">
                {filter === 'pending'
                  ? 'Nenhuma avaliação aguardando moderação. ✓'
                  : 'Nenhuma avaliação nesse filtro.'}
              </CardContent>
            </Card>
          )}
          {filtered.map((r) => {
            const status = (r.moderationStatus ?? 'pending') as 'pending' | 'approved' | 'rejected';
            return (
              <Card key={r.reviewId}>
                <CardContent className="pt-6 flex items-start gap-4">
                  <div className="flex items-center gap-1 text-sm font-medium">
                    {r.rating}
                    <Star className="h-4 w-4 fill-accent text-accent" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      {status === 'pending' && <Badge variant="warning">Pendente</Badge>}
                      {status === 'approved' && <Badge variant="success">Aprovada</Badge>}
                      {status === 'rejected' && <Badge variant="muted">Rejeitada</Badge>}
                      {r.moderationReason && (
                        <span className="text-xs text-muted-foreground italic">
                          ({r.moderationReason})
                        </span>
                      )}
                    </div>
                    {r.comment && <p className="text-sm text-pretty">{r.comment}</p>}
                    {r.lawyerResponse && (
                      <p className="mt-2 text-xs text-muted-foreground border-l-2 border-accent/40 pl-3">
                        <span className="font-medium">Resposta do advogado:</span> {r.lawyerResponse}
                      </p>
                    )}
                    <p className="mt-2 text-xs text-muted-foreground">
                      Advogado <span className="font-mono">{r.lawyerId.slice(0, 8)}…</span> · Tarefa{' '}
                      <span className="font-mono">{r.taskId?.slice(0, 8) ?? '?'}…</span> ·{' '}
                      {formatRelativeDate(r.createdAt)}
                    </p>
                  </div>
                  <div className="flex flex-col gap-2">
                    {status !== 'approved' && (
                      <Button
                        size="sm"
                        variant="accent"
                        onClick={() => moderateMut.mutate({ r, decision: 'approved' })}
                        loading={moderateMut.isPending && moderateMut.variables?.r.reviewId === r.reviewId}
                        className="gap-1"
                      >
                        <Check className="h-3.5 w-3.5" /> Aprovar
                      </Button>
                    )}
                    {status !== 'rejected' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const reason = window.prompt('Motivo da rejeição (opcional)') ?? undefined;
                          moderateMut.mutate({
                            r,
                            decision: 'rejected',
                            ...(reason ? { reason } : {}),
                          });
                        }}
                        className="gap-1"
                      >
                        <X className="h-3.5 w-3.5" /> Rejeitar
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        if (confirm('Remover esta avaliação? Esta ação não pode ser desfeita.')) {
                          delMut.mutate(r);
                        }
                      }}
                      className="text-danger hover:text-danger gap-1"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Deletar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </AdminShell>
  );
}
